// actor — the jargon-free "who" chip, shared across the surface's lenses.
//
// The log's `actor_author | actor_agent | actor_system` is projected to a
// `TimelineActor` token at the shell; here it becomes a calm labelled chip —
// "You" / "The assistant" / "the app" — never the discriminant. This is the
// ai-transparency marking: an agent moment is visibly the assistant's.
//
// The actor is distinguished by a per-actor ICON + its own label, NOT by hue:
// the neutral Badge tone is used throughout because the agent-tinted tone fails
// WCAG 1.4.3 contrast (4.08:1) at chip size in the light palette. Colour was
// never meant to be the sole channel here anyway — the distinct glyph (person /
// sparkle / gear) plus the spelled-out label carry the distinction, so dropping
// the hue costs nothing and keeps the chip a11y-clean.
//
// Pure/props-driven: the only input is the actor token; the label text is the
// component's own (i18n via Lingui), the icon the distinguishing channel.

export { ActorChip };

import { useLingui } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { Badge } from "../Badge/Badge.js";
import { Icon } from "../Icon/Icon.js";
import type { TimelineActor } from "./types.js";

const actorGlyph: Record<TimelineActor, ReactNode> = {
    you: <Icon name="person" size="xs" />,
    assistant: <Icon name="sparkle" size="xs" />,
    app: <Icon name="gear" size="xs" />,
};

const ActorChip = ({ actor }: { readonly actor: TimelineActor }): ReactNode => {
    const { t } = useLingui();
    const label: Record<TimelineActor, string> = {
        you: t`You`,
        assistant: t`The assistant`,
        app: t`the app`,
    };
    return (
        <Badge tone="neutral" icon={actorGlyph[actor]}>
            {label[actor]}
        </Badge>
    );
};
